/**
 * Poll the community detection endpoint until a result is ready.
 *
 * Sends the initial request with the entrez body, then polls
 * (without body) on 202 until the backend returns a non-202 response.
 * Throws on 4xx/5xx errors or after maxAttempts (default 240 = 20min
 * at 5s intervals, matching the backend COMMUNITY_TASK_TIMEOUT).
 *
 * Supports AbortSignal for cancellation. This fires when the
 * fmdMachine transitions *out of* the fetchingCommunity state
 * (e.g. a new submission preempts the current one, or the task
 * completes / errors). NOTE: it does NOT fire on browser-level
 * navigation — fmdService is an application-level singleton
 * (see App.js), so route changes do not trigger state transitions.
 * In-flight polls keep running in that case; the user will see
 * the result if they return to the FMD page before the backend
 * task finishes or the 20-min timeout expires.
 */
// eslint-disable-next-line import/prefer-default-export
export async function pollCommunityResult(
  url: string,
  body?: string,
  pollIntervalMs = 5000,
  maxAttempts = 240,
  signal?: AbortSignal,
): Promise<unknown> {
  // Bail before issuing any network request if we're already aborted.
  // Relying on fetch() to throw AbortError synchronously is not
  // guaranteed across polyfills and test harnesses, so check here.
  if (signal?.aborted) {
    throw signal.reason || new DOMException('aborted', 'AbortError');
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const firstResponse = await fetch(url, {
    method: 'POST',
    body,
    headers,
    signal,
  });

  if (firstResponse.status === 200) {
    return firstResponse.json();
  }

  if (firstResponse.status !== 202) {
    throw new Error(`Community detection failed: ${firstResponse.status}`);
  }

  // Poll without body until result is ready
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(signal.reason || new DOMException('aborted', 'AbortError'));
        return;
      }

      let onAbort: (() => void) | undefined;
      const timer = setTimeout(() => {
        if (signal && onAbort) signal.removeEventListener('abort', onAbort);
        resolve();
      }, pollIntervalMs);

      if (signal) {
        onAbort = () => {
          clearTimeout(timer);
          reject(signal.reason || new DOMException('aborted', 'AbortError'));
        };
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });

    let pollResponse: Response;
    try {
      // eslint-disable-next-line no-await-in-loop
      pollResponse = await fetch(url, { method: 'POST', signal });
    } catch (err) {
      // Re-throw AbortError immediately; retry transient network errors
      if (err instanceof Error && err.name === 'AbortError') throw err;
      // eslint-disable-next-line no-continue
      continue;
    }

    if (pollResponse.status === 200) {
      return pollResponse.json();
    }

    // Treat 502/503/504 as transient (nginx gateway errors).
    // 504 in particular is the common symptom of the long-task
    // problem this polling design solves.
    if ([502, 503, 504].includes(pollResponse.status)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (pollResponse.status !== 202) {
      throw new Error(`Community detection failed: ${pollResponse.status}`);
    }
  }

  throw new Error('Community detection timed out');
}
